// ==================== ORGANIZATION SETTINGS PAGE ====================

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  Mail, 
  Phone,
  MapPin,
  Globe,
  Briefcase,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui';
import { useAuth } from '../../../contexts/auth';
import { authService } from '../../../services';

// ==================== VALIDATION SCHEMA ====================

const organizationSchema = z.object({
  businessName: z.string().min(2, 'Business name must be at least 2 characters').max(100),
  businessEmail: z.string().email('Invalid email address'),
  businessPhone: z.string().max(20).optional().or(z.literal('')),
  businessAddress: z.string().max(255).optional().or(z.literal('')),
  businessWebsite: z.string().url('Invalid URL').optional().or(z.literal('')),
  industry: z.string().max(100).optional().or(z.literal('')),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

// ==================== MAIN COMPONENT ====================

const OrganizationPage = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      businessName: user?.organization?.businessName || '',
      businessEmail: user?.organization?.businessEmail || '',
      businessPhone: user?.organization?.businessPhone || '',
      businessAddress: user?.organization?.businessAddress || '',
      businessWebsite: user?.organization?.businessWebsite || '',
      industry: user?.organization?.industry || '',
    },
  });

  // Reset form when user data changes
  useEffect(() => {
    if (user?.organization) {
      reset({
        businessName: user.organization.businessName || '',
        businessEmail: user.organization.businessEmail || '',
        businessPhone: user.organization.businessPhone || '',
        businessAddress: user.organization.businessAddress || '',
        businessWebsite: user.organization.businessWebsite || '',
        industry: user.organization.industry || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: OrganizationFormData) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await authService.updateOrganization({
        businessName: data.businessName,
        businessEmail: data.businessEmail,
        businessPhone: data.businessPhone || undefined,
        businessAddress: data.businessAddress || undefined,
        businessWebsite: data.businessWebsite || undefined,
        industry: data.industry || undefined,
      });

      if (response.success && response.data) {
        updateUser(response.data);
        setSuccess('Organization information updated successfully');
        reset(data);
      } else {
        setError(response.message || 'Failed to update organization');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Update organization error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-orange-500" />
          Organization Information
        </h2>
        <p className="text-sm text-(--color-muted-gray) mt-1">
          Manage your business details and contact information
        </p>
      </div>

      {/* Status Messages */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm"
          >
            <CheckCircle className="w-4 h-4 shrink-0" />
            {success}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Organization Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F1419] border border-gray-800 rounded-xl p-5"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Business Name & Email Row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Business Name *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Your business name"
                  {...register('businessName')}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                             focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                             ${errors.businessName ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                />
              </div>
              {errors.businessName && (
                <p className="text-xs text-red-400 mt-1">{errors.businessName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Business Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  placeholder="contact@business.com"
                  {...register('businessEmail')}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                             focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                             ${errors.businessEmail ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                />
              </div>
              {errors.businessEmail && (
                <p className="text-xs text-red-400 mt-1">{errors.businessEmail.message}</p>
              )}
            </div>
          </div>

          {/* Phone & Website Row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Business Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  {...register('businessPhone')}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                             focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                             ${errors.businessPhone ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                />
              </div>
              {errors.businessPhone && (
                <p className="text-xs text-red-400 mt-1">{errors.businessPhone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  placeholder="https://www.yourbusiness.com"
                  {...register('businessWebsite')}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                             focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                             ${errors.businessWebsite ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                />
              </div>
              {errors.businessWebsite && (
                <p className="text-xs text-red-400 mt-1">{errors.businessWebsite.message}</p>
              )}
            </div>
          </div>

          {/* Industry */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1.5">
              Industry
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="e.g., Technology, Healthcare, Retail"
                {...register('industry')}
                className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                           focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
                           ${errors.industry ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
              />
            </div>
            {errors.industry && (
              <p className="text-xs text-red-400 mt-1">{errors.industry.message}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1.5">
              Business Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <textarea
                placeholder="Enter your full business address"
                rows={3}
                {...register('businessAddress')}
                className={`w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500 
                           focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all resize-none
                           ${errors.businessAddress ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
              />
            </div>
            {errors.businessAddress && (
              <p className="text-xs text-red-400 mt-1">{errors.businessAddress.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={saving || !isDirty}
              className="min-w-[140px]"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>

      {/* Additional Info */}
      <div className="bg-[#0F1419]/50 border border-gray-800/50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
            <AlertCircle className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-white mb-1">Organization Details</h4>
            <p className="text-xs text-gray-400">
              This information is used for billing, invoices, and communication purposes.
              Make sure to keep your contact details up to date to receive important notifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationPage;
